'use client';

import { useTransition } from 'react';
import { useSearchParams } from 'next/navigation';
import { Link, useRouter } from '@/i18n/routing';
import { DEFAULT_LOGIN_REDIRECT } from '@/routes';
import { authClient } from '@/server/auth-client';
import { useLoginSchema, type TLoginSchema } from '@/services/schemas/auth.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoaderCircleIcon, OctagonAlert } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { LoginErrorCodeEnum } from '@/types/user';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { CardWrapper } from '@/components/auth/card-wrapper';
import { AlertBanner } from '@/components/custom-ui/alert-banner';
import { PasswordInput } from '@/components/custom-ui/password-input';

const LoginForm = () => {
  const router = useRouter();
  const t = useTranslations('auth.loginForm');
  const searchParams = useSearchParams();
  const errorQuery = searchParams.get('error');
  const callbackUrl = searchParams.get('callbackUrl');

  const [isPending, startTransition] = useTransition();

  const loginSchema = useLoginSchema();

  const form = useForm<TLoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  });

  const onSubmit = (values: TLoginSchema) => {
    startTransition(async () => {
      const toastId = toast('login-toast');

      await authClient.signIn.email(
        {
          email: values.email,
          password: values.password,
          rememberMe: values.remember,
          callbackURL: callbackUrl ?? DEFAULT_LOGIN_REDIRECT,
        },
        {
          onRequest: () => {
            toast.loading(t('loading'), { id: toastId });
          },
          onSuccess: (ctx) => {
            if (ctx.data?.twoFactorRedirect) {
              router.push('/auth/login/2fa');
            }
            toast.success(t('success'), { id: toastId });
          },
          onError: (ctx: { error: Error }) => {
            toast.error(t('error', { error: ctx.error.message ?? 'N/A' }), { id: toastId });
          },
        }
      );
    });
  };

  return (
    <div className="flex flex-col gap-4 items-center">
      {errorQuery === LoginErrorCodeEnum.UNAUTHENTICATED && (
        <AlertBanner title={t('errors.unauthenticated')} description={t('errors.unauthenticatedDescription')} variant="error" icon={<OctagonAlert className="h-6 w-6 text-red-500" />} />
      )}
      {errorQuery === LoginErrorCodeEnum.TENANT_NOT_AUTHORIZED && (
        <AlertBanner title={t('errors.tenant')} description={t('errors.tenantDescription')} variant="error" icon={<OctagonAlert className="h-6 w-6 text-red-500" />} />
      )}
      {errorQuery === LoginErrorCodeEnum.INVITATION_REQUIRED_AUTH && (
        <AlertBanner title={t('errors.invitationRequired')} description={t('errors.invitationRequiredDescription')} variant="error" icon={<OctagonAlert className="h-6 w-6 text-red-500" />} />
      )}

      <CardWrapper
        headerTitle={t('headerTitle')}
        headerLabel={t('headerLabel')}
        backButtonLabel={t('noAccount')}
        backButtonHref={
          errorQuery === LoginErrorCodeEnum.INVITATION_REQUIRED_AUTH && callbackUrl ? { pathname: '/auth/register', query: { callbackUrl, invitationRequired: true } } : '/auth/register'
        }>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.email')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isPending} placeholder={t('placeholders.email')} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.password')}</FormLabel>
                    <FormControl>
                      <PasswordInput {...field} disabled={isPending} placeholder={t('placeholders.password')} type="password" />
                    </FormControl>
                    <div className="flex justify-between w-full">
                      <Button size="sm" variant="link" asChild className="px-0 font-normal">
                        <Link href="/auth/reset">{t('forgotPassword')}</Link>
                      </Button>
                      <FormField
                        control={form.control}
                        name="remember"
                        render={({ field }) => (
                          <FormItem className=" flex items-center gap-2">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} id="remember" />
                            </FormControl>
                            <FormLabel htmlFor="remember" className="text-sm font-normal cursor-pointer">
                              {t('fields.remember')}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button disabled={isPending} type="submit" className="w-full">
              {t('actions.login')}
              {isPending && <LoaderCircleIcon className="animate-spin" />}
            </Button>
          </form>
        </Form>
      </CardWrapper>
    </div>
  );
};

export default LoginForm;
